<template>
  <div>
    <div class="sm:flex sm:items-center mb-6 sm:justify-between">
      <h3 class="text-lg leading-6 font-medium text-gray-900">
        Sales Performance
      </h3>
      <div class="mt-3 flex sm:mt-0">
        <div class="relative inline-block text-left z-10">
          <HUMenu>
            <HUMenuButton
              class="inline-flex justify-center w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-indigo-500"
            >
              {{ dateFriendly }}
              <ChevronDownIcon class="-mr-1 ml-2 h-5 w-5" />
            </HUMenuButton>
            <HUMenuItems
              class="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black/5"
            >
              <HUMenuItem>
                <button
                  type="button"
                  class="block px-4 py-2 cursor-pointer text-sm text-gray-700 text-left w-full hover:bg-gray-100 hover:text-gray-900"
                  @click="load('-7 days', 'Last 7 days')"
                >
                  Last 7 days
                </button>
              </HUMenuItem>
              <HUMenuItem>
                <button
                  type="button"
                  class="block px-4 py-2 cursor-pointer text-sm text-gray-700 text-left w-full hover:bg-gray-100 hover:text-gray-900"
                  @click="load('-30 days', 'Last 30 days')"
                >
                  Last 30 days
                </button>
              </HUMenuItem>
              <HUMenuItem>
                <button
                  type="button"
                  class="block px-4 py-2 cursor-pointer text-sm text-gray-700 text-left w-full hover:bg-gray-100 hover:text-gray-900"
                  @click="load('-90 days', 'Last 90 days')"
                >
                  Last 90 days
                </button>
              </HUMenuItem>
            </HUMenuItems>
          </HUMenu>
        </div>
      </div>
    </div>

    <Skeleton v-if="loading" />

    <div v-if="!loading">
      <div
        class="mt-4 mb-4 text-sm text-gray-500 block sm:flex justify-between"
      >
        <p class="hidden sm:block">
          All orders for the period selected.
          <a
            href="https://docs.sparklayer.io/orders"
            target="_blank"
            rel="noopener noreferrer"
            class="font-medium inline-flex items-center text-indigo-600 hover:text-indigo-500"
          >
            <span>Learn more</span>
            <ExternalLinkIcon class="group-hover:text-spark-purple ml-1 h-4 w-4" />
          </a>
        </p>
        <div class="flex items-center">
          <div class="flex items-center mr-5">
            <span class="relative mr-1.5 inline-flex rounded-full h-3 w-3 bg-blue-500" />
            <span>This period</span>
          </div>
          <div class="flex items-center">
            <span class="relative mr-1.5 inline-flex rounded-full h-3 w-3 bg-green-500" />
            <span>Last period</span>
          </div>
        </div>
      </div>
      <VueApexCharts
        height="200"
        type="line"
        :options="chart.options"
        :series="chart.series"
        class="z-0"
      />
      <div
        v-if="env.platform === 'shopify' || env.platform === 'shopify_plus'"
        class="mt-0 mb-4 text-xs text-gray-500 block sm:flex justify-between"
      >
        Please note: Draft orders aren't shown in your sales performance.
      </div>
      <dl
        class="mt-4 grid grid-cols-1 rounded-lg bg-gray-50 overflow-hidden shadow divide-y divide-gray-200 md:grid-cols-3 md:divide-y-0 md:divide-x"
      >
        <div>
          <div class="px-4 py-5 sm:p-6">
            <dt class="text-sm font-normal text-gray-600">
              Revenue for period
            </dt>
            <dd class="mt-1 flex justify-between items-baseline md:block lg:flex">
              <div class="flex items-baseline text-2xl font-semibold text-gray-600">
                {{ formatMoney(currentRevenue / 100, currencyCode) }}
              </div>

              <IncreaseDecreaseLabels
                :current="currentRevenue"
                :previous="previousRevenue"
              />
            </dd>
          </div>
        </div>

        <div>
          <div class="px-4 py-5 sm:p-6">
            <dt class="text-sm font-normal text-gray-600">
              Average order value
            </dt>
            <dd class="mt-1 flex justify-between items-baseline md:block lg:flex">
              <div class="flex items-baseline text-2xl font-semibold text-gray-600">
                {{ formatMoney(currentAverage / 100, currencyCode) }}
              </div>
              <IncreaseDecreaseLabels
                :current="currentAverage"
                :previous="previousAverage"
              />
            </dd>
          </div>
        </div>

        <div>
          <div class="px-4 py-5 sm:p-6">
            <dt class="text-sm font-normal text-gray-600">
              Number of orders
            </dt>
            <dd class="mt-1 flex justify-between items-baseline md:block lg:flex">
              <div class="flex items-baseline text-2xl font-semibold text-gray-600">
                {{ currentOrders }}
              </div>
              <IncreaseDecreaseLabels
                :current="currentOrders"
                :previous="previousOrders"
              />
            </dd>
          </div>
        </div>
      </dl>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue3-apexcharts';
import { mapGetters } from 'vuex';
import {
  Menu as HUMenu, MenuButton as HUMenuButton, MenuItem as HUMenuItem, MenuItems as HUMenuItems,
} from '@headlessui/vue';
import { ChevronDownIcon, ExternalLinkIcon } from '@heroicons/vue/outline';
import formatMoney from '@/utils/formatMoney';
import api from '@/utils/api';
import Skeleton from '@/components/Skeleton.vue';
import IncreaseDecreaseLabels from '@/components/IncreaseDecreaseLabels.vue';

export default {
  name: 'OrdersSalesPerformance',
  components: {
    VueApexCharts,
    IncreaseDecreaseLabels,
    Skeleton,
    ChevronDownIcon,
    ExternalLinkIcon,
    HUMenu,
    HUMenuButton,
    HUMenuItems,
    HUMenuItem,
  },
  data() {
    const moneyFormatterWithoutPence = new Intl.NumberFormat([], {
      style: 'currency',
      currency: 'GBP',
      maximumFractionDigits: 0,
    });
    const dateFormatter = new Intl.DateTimeFormat([], { month: 'short', day: 'numeric' });
    return {
      loading: true,
      data: null,
      dateFriendly: 'Last 30 days',
      dateFrom: '-30 days',
      dateTo: 'today',
      currencyCode: 'usd',
      formatMoney,
      dateFormatter,
      moneyFormatterWithoutPence,
    };
  },
  computed: {
    ...mapGetters({
      env: 'env/get',
    }),
    currentRevenue() {
      return this.data.current
        .reduce((accumulator, currentValue) => accumulator + currentValue.sumOfOrders, 0);
    },
    currentOrders() {
      return this.data.current
        .reduce((accumulator, currentValue) => accumulator + currentValue.numberOfOrders, 0);
    },
    currentAverage() {
      if (this.currentRevenue === 0 && this.currentOrders === 0) {
        return 0;
      }
      return this.currentRevenue / this.currentOrders;
    },
    previousRevenue() {
      return this.data.previous
        .reduce((accumulator, currentValue) => accumulator + currentValue.sumOfOrders, 0);
    },
    previousOrders() {
      return this.data.previous
        .reduce((accumulator, currentValue) => accumulator + currentValue.numberOfOrders, 0);
    },
    previousAverage() {
      if (this.previousRevenue === 0 && this.previousOrders === 0) {
        return 0;
      }
      return this.previousRevenue / this.previousOrders;
    },
    chart() {
      return {
        options: {
          stroke: {
            curve: 'smooth',
          },
          markers: {
            size: 5,
          },
          legend: {
            show: false,
          },
          chart: {
            toolbar: {
              show: false,
            },
          },
          xaxis: {
            categories: this.data.current.map((x) => this.dateFormatter.format(new Date(x.date))),
            labels: {
              show: this.data.current.length < 31,
            },
          },
          yaxis: {
            labels: {
              show: true,
              formatter: (value) => this.moneyFormatterWithoutPence.format(value),
            },
          },
        },
        series: [
          {
            name: 'Current - Revenue',
            data: this.data.current.map((x) => x.sumOfOrders / 100),
          },
          {
            name: 'Previous - Revenue',
            data: this.data.previous.map((x) => x.sumOfOrders / 100),
          },
        ],
      };
    },
  },
  mounted() {
    this.load('-30 days', 'Last 30 days');
  },
  methods: {
    async load(dateFrom, dateFriendly) {
      this.loading = true;
      this.dateFrom = dateFrom;
      this.dateFriendly = dateFriendly;
      this.data = (await api.get(`/${this.env.environment}/orders-stats`, {
        params: {
          date_to: this.dateTo,
          date_from: this.dateFrom,
        },
      })).data;
      this.currencyCode = this.data.current[0].currencyCode;
      this.moneyFormatterWithoutPence = new Intl.NumberFormat([], {
        style: 'currency',
        currency: this.currencyCode,
        maximumFractionDigits: 0,
      });
      this.loading = false;
    },
  },
};
</script>
